import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Search, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeagueSelector = ({ onSyncComplete }) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeagues, setSelectedLeagues] = useState(new Set());
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [presets, setPresets] = useState({
    england: false,
    topEuropean: false,
    allEuropeanFirst: false,
    international: false
  });

  // Predefined league lists
  const euroCountries = [
    'England', 'Spain', 'Italy', 'Germany', 'France', 'Portugal', 'Netherlands',
    'Belgium', 'Scotland', 'Turkey', 'Russia', 'Ukraine', 'Austria', 'Switzerland',
    'Greece', 'Czech-Republic', 'Denmark', 'Sweden', 'Norway', 'Poland', 'Romania',
    'Croatia', 'Serbia', 'Bulgaria', 'Hungary'
  ];

  const topLeagues = [
    'Premier League', 'La Liga', 'Serie A', 'Bundesliga', 'Ligue 1',
    'Primeira Liga', 'Eredivisie', 'Championship', 'UEFA Champions League',
    'UEFA Europa League', 'World Cup', 'European Championship'
  ];

  useEffect(() => {
    fetchAvailableLeagues();
    loadSavedSelections();
  }, []);

  const loadSavedSelections = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/saved-league-selections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.selected_league_ids && response.data.selected_league_ids.length > 0) {
        setSelectedLeagues(new Set(response.data.selected_league_ids));
        toast.info(`Loaded ${response.data.selected_league_ids.length} previously selected leagues`);
      }
    } catch (error) {
      console.error('Error loading saved selections:', error);
    }
  };

  const fetchAvailableLeagues = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/api-football-leagues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAvailableLeagues(response.data.leagues);
      toast.success(`Loaded ${response.data.total} leagues from API-Football`);
    } catch (error) {
      console.error('Error fetching leagues:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch leagues from API-Football. Make sure API key is configured.');
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (preset) => {
    const newPresets = { ...presets, [preset]: !presets[preset] };
    setPresets(newPresets);
    
    const newSelected = new Set(selectedLeagues);
    
    if (preset === 'england' && newPresets.england) {
      // Select all England leagues
      availableLeagues
        .filter(l => l.country === 'England')
        .forEach(l => newSelected.add(l.id));
    } else if (preset === 'topEuropean' && newPresets.topEuropean) {
      // Select top 5 European leagues
      availableLeagues
        .filter(l => topLeagues.includes(l.name))
        .forEach(l => newSelected.add(l.id));
    } else if (preset === 'international' && newPresets.international) {
      // Select international competitions
      availableLeagues
        .filter(l => l.type === 'Cup' || l.country === 'World' || l.country === 'Europe')
        .forEach(l => newSelected.add(l.id));
    }
    
    setSelectedLeagues(newSelected);
  };

  const toggleLeague = (leagueId) => {
    const newSelected = new Set(selectedLeagues);
    if (newSelected.has(leagueId)) {
      newSelected.delete(leagueId);
    } else {
      newSelected.add(leagueId);
    }
    setSelectedLeagues(newSelected);
  };

  const handleSync = async () => {
    if (selectedLeagues.size === 0) {
      toast.error('Please select at least one league');
      return;
    }

    const confirmed = window.confirm(
      `This will sync ${selectedLeagues.size} leagues and their teams from API-Football. This may take a few minutes. Continue?`
    );

    if (!confirmed) return;

    setSyncing(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/admin/sync-selected-leagues`,
        {
          league_ids: Array.from(selectedLeagues)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Successfully synced ${response.data.stats.leagues_synced} leagues and ${response.data.stats.teams_synced} teams!`);
      
      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (error) {
      console.error('Error syncing leagues:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync leagues');
    } finally {
      setSyncing(false);
    }
  };

  const filteredLeagues = availableLeagues.filter(league =>
    league.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    league.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Card className="glass-dark border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-400" />
          Select Leagues to Sync from API-Football
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-semibold text-white mb-1">API-Football Sync</p>
              <p>Select the leagues you want to sync. Only selected leagues and their teams will be imported from API-Football. This avoids loading unnecessary data.</p>
            </div>
          </div>
        </div>

        {/* Presets */}
        <div>
          <Label className="text-white mb-3 block">Quick Presets:</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors">
              <Checkbox
                id="england"
                checked={presets.england}
                onCheckedChange={() => handlePresetChange('england')}
              />
              <label htmlFor="england" className="text-sm text-white cursor-pointer">
                All England Leagues (6)
              </label>
            </div>
            
            <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors">
              <Checkbox
                id="topEuropean"
                checked={presets.topEuropean}
                onCheckedChange={() => handlePresetChange('topEuropean')}
              />
              <label htmlFor="topEuropean" className="text-sm text-white cursor-pointer">
                Top 5 European Leagues
              </label>
            </div>
            
            <div className="flex items-center space-x-2 bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors">
              <Checkbox
                id="international"
                checked={presets.international}
                onCheckedChange={() => handlePresetChange('international')}
              />
              <label htmlFor="international" className="text-sm text-white cursor-pointer">
                International Competitions
              </label>
            </div>
          </div>
        </div>

        {/* Search */}
        <div>
          <Label className="text-white mb-2 block">Search Leagues:</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by name or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>

        {/* League List */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-white">
              Available Leagues ({selectedLeagues.size} selected):
            </Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedLeagues(new Set())}
              className="text-gray-400 hover:text-white"
            >
              Clear All
            </Button>
          </div>
          
          <div className="max-h-96 overflow-y-auto space-y-2 bg-black/20 rounded-lg p-3">
            {filteredLeagues.map((league) => (
              <div
                key={league.id}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  selectedLeagues.has(league.id)
                    ? 'bg-blue-500/20 border border-blue-500/50'
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id={`league-${league.id}`}
                    checked={selectedLeagues.has(league.id)}
                    onCheckedChange={() => toggleLeague(league.id)}
                  />
                  <label
                    htmlFor={`league-${league.id}`}
                    className="text-sm text-white cursor-pointer"
                  >
                    <span className="font-medium">{league.name}</span>
                    <span className="text-gray-400 ml-2">({league.country})</span>
                  </label>
                </div>
                {selectedLeagues.has(league.id) && (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                )}
              </div>
            ))}
            
            {filteredLeagues.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                No leagues found matching your search
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={async () => {
              try {
                const token = localStorage.getItem('session_token');
                await axios.post(
                  `${API}/admin/save-league-selections`,
                  Array.from(selectedLeagues),
                  {
                    headers: { 
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  }
                );
                toast.success('Selections saved! You can come back later to add more.');
              } catch (error) {
                toast.error('Failed to save selections');
              }
            }}
            disabled={selectedLeagues.size === 0}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/10"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Save Selections ({selectedLeagues.size})
          </Button>
          
          <Button
            onClick={handleSync}
            disabled={syncing || selectedLeagues.size === 0}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {syncing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Syncing...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Sync & Delete Others ({selectedLeagues.size})
              </>
            )}
          </Button>
        </div>

        {/* Note */}
        <div className="text-xs text-gray-400 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <strong>Important:</strong> This will sync ONLY the leagues you select and DELETE all others from the database. 
          This keeps your database clean and improves performance. You can always come back and add more leagues later.
        </div>
      </CardContent>
    </Card>
  );
};

export default LeagueSelector;
